/**
 * Gift Card Payment Manager Module (Phase 3)
 * Lets the customer apply a gift card as a payment method in checkout:
 * validates card number + PIN against the anonymous checkout endpoint and
 * shows the deducted amount as its own order row.
 *
 * Rules pinned here:
 * - B6: max ONE card per order; the client NEVER sends an amount - the server
 *   computes min(balance, order total after discounts + shipping).
 * - B10: the deduction is applied LAST - it never affects VAT or discounts.
 * - The PIN is kept in memory only, sent only to the validate endpoint and in
 *   the initiate call. NEVER logged, NEVER persisted (no localStorage /
 *   sessionStorage) and NEVER sent to a payment provider.
 * - Nothing is stored between page loads: an applied card cannot survive a
 *   reload because the PIN is required for initiate and must not be stored.
 * - The validation failure message is deliberately generic (E1): the same
 *   text for unknown card, wrong PIN, locked card and wrong merchant.
 */

import { getStorage } from '../core/storage.js';
import { formatPrice, getCartType } from '../core/utils.js';
import { t } from '../core/i18n.js';

// Display-only mirror of the server-side B5 minimum charge amounts
// (GiftCardPaymentCalculator): Stripe 3 SEK/3 NOK, Bambora 1, None 0 (zero
// remainder allowed). The server is always authoritative - these constants
// only make the displayed amount match what the server will compute.
const PROVIDER_MINIMUMS = {
    stripe: 3,
    bambora: 1,
    klarna: 1,
    none: 0
};

export class GiftCardPaymentManager {
    constructor(system) {
        this.system = system;
        this.config = {
            validateUrl: '/cart/api/v1/checkout/giftcard/validate',
            merchantId: system.config.checkout?.merchantId || system.config.merchantId || null,
            provider: system.config.checkout?.provider || 'klarna'
        };

        // Applied card - memory only, never persisted. Shape:
        // { cardNumber, pinCode, balance }
        this.applied = null;
        this.currentAmount = 0;
        this.currentRemainder = 0;
        this.blocked = false;
        this.infoShown = false;
        this.isValidating = false;
        this.elements = {};

        console.log('GiftCardPaymentManager: Initialized');
    }

    init() {
        this.findElements();

        if (!this.elements.section) {
            return; // Template without the gift card section - nothing to do
        }

        // The section is opt-in per merchant via the published config
        // (GiftCardPaymentEnabled, Phase 4). Default: hidden - zero change
        // for merchants without the feature.
        const enabled = this.system.config.checkout?.giftCardPaymentEnabled === true;
        const provider = (this.config.provider || '').toLowerCase();

        // Klarna merchants never get gift card payments in this delivery
        // (server-side block in initiate), so the section is never shown.
        const cartItems = getStorage(this.system.config.storageKey) || [];
        const cartType = getCartType(cartItems);

        if (!enabled || provider === 'klarna' || cartType === 'only-giftcards') {
            this.elements.section.style.display = 'none';
            return;
        }

        this.elements.section.style.display = 'block';
        this.bindEvents();
        this.updateRowVisibility();
    }

    findElements() {
        this.elements = {
            section: document.getElementById('gift-card-payment-section'),
            inputRow: document.getElementById('gift-card-input-row'),
            cardNumberInput: document.getElementById('gift-card-number'),
            pinInput: document.getElementById('gift-card-pin'),
            applyButton: document.getElementById('apply-gift-card'),
            appliedRow: document.getElementById('gift-card-applied-row'),
            appliedLabel: document.getElementById('gift-card-applied-label'),
            removeButton: document.getElementById('remove-gift-card'),
            message: document.getElementById('gift-card-message'),
            orderRow: document.getElementById('order-gift-card-row'),
            orderAmount: document.getElementById('order-gift-card-amount'),
            placeOrderBtn: document.getElementById('place-order-btn')
        };
    }

    bindEvents() {
        if (this.elements.applyButton) {
            this.elements.applyButton.addEventListener('click', () => this.applyGiftCard());
        }
        if (this.elements.removeButton) {
            this.elements.removeButton.addEventListener('click', () => this.removeGiftCard());
        }
        // Apply on Enter in either field
        [this.elements.cardNumberInput, this.elements.pinInput].forEach(input => {
            if (input) {
                input.addEventListener('keypress', (e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        this.applyGiftCard();
                    }
                });
            }
        });
    }

    isApplied() {
        return this.applied !== null;
    }

    /**
     * Gift card payment entries for InitiatePaymentRequest.GiftCardPayments.
     * The amount is never sent - the server computes it (B6).
     */
    async applyGiftCard() {
        if (this.isValidating || this.applied) {
            return;
        }

        const cardNumber = (this.elements.cardNumberInput?.value || '').trim();
        const pinCode = (this.elements.pinInput?.value || '').trim();

        if (!cardNumber || !pinCode) {
            this.showMessage(t('giftCardPayment.enterCard'), 'error');
            return;
        }

        this.isValidating = true;
        this.showMessage(t('giftCardPayment.validating'), 'info');

        try {
            const response = await fetch(this.config.validateUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // PIN is sent here and in initiate ONLY - never logged, never stored
                body: JSON.stringify({
                    merchantId: this.config.merchantId,
                    cardNumber: cardNumber,
                    pinCode: pinCode
                })
            });

            if (!response.ok) {
                // Same generic message as for a rejected card (E1)
                this.showMessage(t('giftCardPayment.invalid'), 'error');
                return;
            }

            const data = await response.json();

            if (!data.valid) {
                // Generic - unknown card, wrong PIN, locked card and wrong
                // merchant all look identical to the customer (E1)
                this.showMessage(t('giftCardPayment.invalid'), 'error');
                return;
            }

            this.applied = { cardNumber, pinCode, balance: data.balance };

            // Clear the PIN field from the DOM - the value lives in memory
            // only, for the initiate call
            if (this.elements.pinInput) {
                this.elements.pinInput.value = '';
            }

            this.showAppliedState();
            // No success message - the summary row and the applied label
            // already show the card is applied (the message row is for
            // errors and "removed" only)
            this.hideMessage();
            this.requestTotalUpdate();
        } catch (error) {
            console.error('GiftCardPaymentManager: Validation request failed');
            this.showMessage(t('giftCardPayment.error'), 'error');
        } finally {
            this.isValidating = false;
        }
    }

    removeGiftCard() {
        this.applied = null;
        this.currentAmount = 0;

        if (this.elements.inputRow) this.elements.inputRow.style.display = 'flex';
        if (this.elements.appliedRow) this.elements.appliedRow.style.display = 'none';
        if (this.elements.cardNumberInput) this.elements.cardNumberInput.value = '';

        // Unblock BEFORE showing the message - unblockOrderButton hides the
        // (blocked) message row and must not clear the "removed" text. Same
        // for the full-coverage info note (Phase 5): clear it first so the
        // recalculate triggered below leaves the "removed" text alone.
        this.unblockOrderButton();
        this.clearInfoMessage();
        this.showMessage(t('giftCardPayment.removed'), 'success');
        this.updateRowVisibility();
        this.requestTotalUpdate();
    }

    showAppliedState() {
        if (this.elements.inputRow) this.elements.inputRow.style.display = 'none';
        if (this.elements.appliedRow) this.elements.appliedRow.style.display = 'flex';
        if (this.elements.appliedLabel) {
            // The customer may see their own full card number (requirement 7)
            this.elements.appliedLabel.textContent =
                `Presentkort ${this.applied.cardNumber} (${t('giftCardPayment.balanceLabel')}: ${formatPrice(this.applied.balance)})`;
        }
    }

    /**
     * Gift card payment entries for InitiatePaymentRequest.GiftCardPayments.
     * The amount is never sent - the server computes it (B6).
     */
    getGiftCardPayments() {
        if (!this.applied) {
            return [];
        }
        return [{
            cardNumber: this.applied.cardNumber,
            pinCode: this.applied.pinCode
        }];
    }

    /**
     * Recomputes the displayed gift card amount from the total BEFORE the
     * gift card deduction and mirrors the server-side B5 rule (display only).
     * Called by the central total calculation on every totals update, so a
     * discount or shipping change after validation is caught immediately.
     * Also owns the order-button state for the one remaining blocked case:
     * - positive remainder on None (server cannot complete it, condition (b))
     * Zero remainder on a card provider is allowed since Phase 5 (routed to
     * /payments/initiate/none at submit) and shows an info message instead.
     *
     * @param {number} totalBeforeGiftCard - Order total incl. VAT, discounts
     *   and shipping, BEFORE the gift card deduction
     * @returns {number} Amount to deduct (0 when nothing is applied)
     */
    recalculate(totalBeforeGiftCard) {
        if (!this.applied) {
            this.currentAmount = 0;
            this.currentRemainder = 0;
            this.clearInfoMessage();
            this.updateRowVisibility();
            return 0;
        }

        const provider = (this.config.provider || '').toLowerCase();
        const minimum = PROVIDER_MINIMUMS[provider] ?? 0;

        // Server: amount = min(balance, total) (B6)
        let amount = Math.min(this.applied.balance, totalBeforeGiftCard);
        let remainder = totalBeforeGiftCard - amount;

        if (remainder > 0 && remainder < minimum) {
            // Server B5: reduce the gift card amount so the remainder lands
            // exactly on the provider minimum
            amount = totalBeforeGiftCard - minimum;
            remainder = minimum;
        }

        amount = Math.max(0, amount);
        this.currentAmount = amount;
        this.currentRemainder = remainder;

        // Blocked state: a partial coverage on the None provider can never
        // complete (server condition (b)), so the customer gets a clear
        // message and a disabled order button instead of a failed order.
        // Full coverage on a card provider is ALLOWED since Phase 5 - the
        // order completes via /payments/initiate/none with no provider call
        // (customer-form.js routes it there) - so the button stays enabled
        // and an info message tells the customer no card payment is needed.
        if (provider === 'none' && remainder > 0) {
            this.clearInfoMessage();
            this.blockOrderButton(t('giftCardPayment.partialNotSupported'));
        } else if (provider !== 'none' && remainder === 0 && amount > 0) {
            this.unblockOrderButton();
            this.showInfoMessage(t('giftCardPayment.fullCoverageInfo'));
        } else {
            this.clearInfoMessage();
            this.unblockOrderButton();
        }

        this.updateRowVisibility();
        return amount;
    }

    /**
     * True when the applied card covers the ENTIRE order (Phase 5). The
     * checkout submit paths use this to route the order to
     * /payments/initiate/none instead of the merchant's card provider.
     * Display-only mirror of the server calculation - the server recomputes
     * the amounts and only allows the anonymous None order when the
     * remainder is exactly 0 (condition (b)).
     */
    isFullCoverage() {
        return this.applied !== null && this.currentAmount > 0 && this.currentRemainder === 0;
    }

    blockOrderButton(message) {
        this.blocked = true;
        if (this.elements.placeOrderBtn) {
            this.elements.placeOrderBtn.disabled = true;
            // The storefront CSS does not style :disabled - make the blocked
            // state visible (dimmed + not-allowed cursor)
            this.elements.placeOrderBtn.style.opacity = '0.5';
            this.elements.placeOrderBtn.style.cursor = 'not-allowed';
        }
        this.showMessage(message, 'error');
    }

    unblockOrderButton() {
        if (!this.blocked) {
            return;
        }
        this.blocked = false;
        if (this.elements.placeOrderBtn) {
            // An applied card implies a non-empty cart (nothing is persisted),
            // so re-enabling here cannot conflict with the empty-cart state
            this.elements.placeOrderBtn.disabled = false;
            this.elements.placeOrderBtn.style.opacity = '';
            this.elements.placeOrderBtn.style.cursor = '';
        }
        this.hideMessage();
    }

    updateRowVisibility() {
        const show = this.applied !== null && this.currentAmount > 0;
        if (this.elements.orderRow) {
            this.elements.orderRow.style.display = show ? 'flex' : 'none';
        }
        if (this.elements.orderAmount && show) {
            this.elements.orderAmount.textContent = `-${formatPrice(this.currentAmount)}`;
        }
    }

    showMessage(text, type) {
        if (!this.elements.message) {
            return;
        }
        this.elements.message.textContent = text;
        this.elements.message.className = `discount-message ${type}`;
        this.elements.message.style.display = 'block';
    }

    hideMessage() {
        if (!this.elements.message) {
            return;
        }
        this.elements.message.textContent = '';
        this.elements.message.style.display = 'none';
    }

    /**
     * Shows the full-coverage info note and marks it as ours, so a later
     * recalculate can clear exactly this message without touching validation
     * errors or other messages shown through showMessage.
     */
    showInfoMessage(text) {
        this.infoShown = true;
        this.showMessage(text, 'info');
    }

    clearInfoMessage() {
        if (!this.infoShown) {
            return;
        }
        this.infoShown = false;
        this.hideMessage();
    }

    /**
     * Runs the central total calculation, which calls recalculate() and
     * rewrites the displayed total with the deduction applied last.
     */
    requestTotalUpdate() {
        document.dispatchEvent(new CustomEvent('requestTotalUpdate', {
            detail: { source: 'giftCardPayment' }
        }));
    }
}
